import {
    AutocompleteRenderInputParams,
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    List,
    ListItem,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseAutoCompleteInput from '@/components/ui/BaseAutoCompleteInput';
import { useAssociationState } from './provider/context';
import { ASSOCIATE_PAIRING_MODE, Profile } from '@/types/pages/storeManagement/device/associate';
import BaseFontIcon from '../ui/BaseFontIcon';
import { isEmpty } from 'lodash';
import { filterLayoutsByPairingMode } from '@/utils/associateUtils';
import { useAssociationHook } from './provider/useAssociationHook';

const SingleAssociationContent = () => {
    const { t } = useTranslation('common');
    const { state, dispatch } = useAssociationState();
    const { prepareSingleProductInfo } = useAssociationHook();
    const { profiles, selectedPairingMode, campaignEnable, noCampaignWording, productLoading } = state;
    const { product, layoutIdRelations } = state.singleAssociateTemp;

    const handleToggle = (profile: Profile) => {
        if (!layoutIdRelations[profile.identifier]) {
            dispatch({ type: 'SET_DIALOG', payload: { key: 'defaultLayout', open: true } });
            dispatch({ type: 'SET_CURRENT_PROFILE', payload: profile });
            return;
        }

        dispatch({ type: 'SET_SINGLE_LAYOUT_RELATIONS', payload: { key: profile.identifier, layoutId: null } });
    };

    return (
        <Box display={'flex'} flexDirection={'column'} width={'100%'}>
            <Box mb={2}>
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                    {t('Pairing Mode')}
                </Typography>
                <FormControl>
                    <RadioGroup
                        row
                        value={selectedPairingMode}
                        onChange={(event) => {
                            const mode = event.target.value as ASSOCIATE_PAIRING_MODE;
                            dispatch({
                                type: 'SET_SELECTED_PAIRING_MODE',
                                payload: mode,
                            });
                            if (mode === ASSOCIATE_PAIRING_MODE.NORMAL) {
                                dispatch({ type: 'SET_NO_CAMPAIGN_WORDING', payload: '' });
                            }
                            if (mode === ASSOCIATE_PAIRING_MODE.IMAGE) {
                                dispatch({ type: 'CLEAR_SINGLE_LAYOUT_RELATIONS' });
                            }
                        }}
                    >
                        <FormControlLabel
                            value={ASSOCIATE_PAIRING_MODE.NORMAL}
                            control={<Radio size="small" />}
                            label={t('Normal')}
                        />
                        {campaignEnable ? (
                            <FormControlLabel
                                value={ASSOCIATE_PAIRING_MODE.IMAGE}
                                control={<Radio size="small" />}
                                label={t('Image')}
                            />
                        ) : null}
                    </RadioGroup>
                </FormControl>
            </Box>

            <Box mb={2}>
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                    {t('Product')}
                </Typography>
                <BaseAutoCompleteInput
                    selectedProduct={product}
                    setSelectedProduct={async (product) => {
                        dispatch({ type: 'SET_SINGLE_PRODUCT', payload: product });
                        await prepareSingleProductInfo(product?.barcode || null);
                    }}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                            {...params}
                            placeholder={t('Search Product by Name, Barcode, or Number')}
                            helperText={t('Please enter at least 3 numbers or 2 characters')}
                            variant="outlined"
                        />
                    )}
                />
            </Box>

            {product ? (
                <>
                    <Box>
                        <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                            {t('Profiles')}
                        </Typography>
                        {productLoading ? (
                            <Box
                                sx={{
                                    height: 120,
                                    borderRadius: 1,
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                }}
                            >
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    {t('Checking profile availability...')}
                                </Typography>
                            </Box>
                        ) : isEmpty(profiles) ? (
                            <Alert severity="warning" sx={{ mt: 0.5 }}>
                                {t('No profiles available for this device.')}
                            </Alert>
                        ) : !isEmpty(profiles) ? (
                            <Box
                                sx={{
                                    borderRadius: 1,
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    maxHeight: 300,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    flexShrink: 0,
                                }}
                            >
                                <List disablePadding>
                                    {Object.entries(profiles).map(([id, profile]) => {
                                        const availableLayouts = filterLayoutsByPairingMode(
                                            profile.layouts,
                                            selectedPairingMode,
                                        );
                                        const hasLayout = availableLayouts.length > 0;
                                        const isDisabled = !product || !hasLayout;

                                        return (
                                            <ListItem
                                                key={id}
                                                sx={{
                                                    height: 48,
                                                    minHeight: 48,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                                    '&:last-of-type': { borderBottom: 'none' },
                                                    opacity: hasLayout ? 1 : 0.6,
                                                    backgroundColor: hasLayout
                                                        ? 'transparent'
                                                        : (theme) => theme.palette.action.hover,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    title={profile.name}
                                                    sx={{
                                                        mr: 2,
                                                        flex: 1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    color={hasLayout ? 'text.primary' : 'text.disabled'}
                                                >
                                                    {profile.name}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        minWidth: 140,
                                                    }}
                                                >
                                                    {hasLayout && product ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL ? (
                                                                <Box
                                                                    sx={{ display: 'flex', alignItems: 'center' }}
                                                                    onClick={(_event) => {
                                                                        _event.stopPropagation();
                                                                        if (isDisabled) return;
                                                                        handleToggle(profile);
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={!layoutIdRelations[id]}
                                                                    />
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{
                                                                            fontSize: 14,
                                                                            lineHeight: 1.2,
                                                                            userSelect: 'none',
                                                                        }}
                                                                    >
                                                                        {t('Use Default')}
                                                                    </Typography>
                                                                </Box>
                                                            ) : (
                                                                <Chip
                                                                    size="small"
                                                                    color="success"
                                                                    variant="outlined"
                                                                    label={t('Campaign Ready')}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <BaseFontIcon
                                                                className="fa-regular fa-circle-exclamation"
                                                                size={18}
                                                            />
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    ml: 1,
                                                                    fontSize: 14,
                                                                    lineHeight: 1.2,
                                                                    color: 'text.disabled',
                                                                    userSelect: 'none',
                                                                }}
                                                            >
                                                                {t('Not Available')}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Box>
                        ) : null}
                    </Box>
                </>
            ) : null}
            {noCampaignWording && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    {noCampaignWording}
                </Alert>
            )}
        </Box>
    );
};

export default SingleAssociationContent;
